import { TIME_ZONE_VALUES } from "common/constants/timezones";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import utcPlugin from "dayjs/plugin/utc";

dayjs.extend(utcPlugin);

export type TimeZoneValue =
  typeof TIME_ZONE_VALUES[keyof typeof TIME_ZONE_VALUES];

/**
 * 12-hour clock values for the hour dropdown.
 * Ordered 1–11 then 0 (where 0 displays as "12").
 */
export const HOURS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 0];

/** Minute values 0–59 for the minute dropdown. */
export const MINUTES = Array.from({ length: 60 }, (_, i) => i);

/**
 * AM/PM period constants.
 * Used as both display values and internal identifiers.
 */
export const PERIODS = {
  AM: "AM",
  PM: "PM",
} as const;

/** Union type of valid period values: `"AM" | "PM"`. */
export type Period = typeof PERIODS[keyof typeof PERIODS];

/**
 * US timezone display strings pinned to the top of the timezone dropdown.
 * Only zones present in the caller-provided timezone list are shown.
 * Canada/Mexico/UK deferred for future iteration.
 */
export const US_PINNED_ZONES: TimeZoneValue[] = [
  "Eastern Standard Time",
  "Central Standard Time",
  "Mountain Standard Time",
  "Pacific Standard Time",
  "Alaskan Standard Time",
  "Hawaiian Standard Time",
  "US Mountain Standard Time",
  "US Eastern Standard Time",
];

/** Select options for the hour dropdown. Value 0 renders label "12". */
export const HOUR_OPTIONS = HOURS.map((value) => ({
  label: String(value === 0 ? 12 : value),
  value,
}));

/** Select options for the minute dropdown. Labels are zero-padded ("00"–"59"). */
export const MINUTE_OPTIONS = MINUTES.map((value) => ({
  label: String(value).padStart(2, "0"),
  value,
}));

/** Select options for the AM/PM period dropdown. */
export const PERIOD_OPTIONS: { label: Period; value: Period }[] = [
  { label: "AM", value: "AM" },
  { label: "PM", value: "PM" },
];

/**
 * Parse an ISO 8601 date string preserving its embedded timezone offset.
 * Equivalent to `moment.parseZone()` — the wall-clock time is kept in the
 * string's own offset instead of being converted to local time.
 *
 * @param dateString - ISO 8601 string (e.g. `"2024-03-10T13:45:00+05:30"`)
 * @returns A Dayjs instance whose `.hour()` etc. reflect the original offset zone.
 */
export const parseZoneDayjs = (dateString: string): Dayjs => {
  const match = dateString.match(/([+-]\d{2}:?\d{2}|Z)$/);
  if (!match) return dayjs(dateString);
  const offset = match[1];
  if (offset === "Z") return dayjs.utc(dateString);
  const sign = offset[0] === "+" ? 1 : -1;
  const parts = offset.slice(1).split(":");
  const offsetMinutes =
    sign * (parseInt(parts[0], 10) * 60 + parseInt(parts[1] || "0", 10));
  // Remove offset suffix, parse as UTC, then shift display
  const withoutTz = dateString.replace(/[+-]\d{2}:?\d{2}$/, "");
  return dayjs.utc(withoutTz).utcOffset(offsetMinutes, true);
};

/**
 * Extract 12-hour time parts (hours, minutes, period) from an ISO 8601 string.
 *
 * @param dateString - ISO 8601 date string
 * @param utc - When `true`, interpret as UTC wall-clock.
 *   When `false`/omitted, preserve the string's embedded offset (parseZone).
 * @returns 12-hour time parts: hours (0–11, where 0 = 12), minutes (0–59), period ("AM"|"PM")
 */
export const parseTimeFromDateString = (
  dateString: string,
  utc?: boolean
): { hours: number; minutes: number; period: Period } => {
  const d = utc ? dayjs.utc(dateString) : parseZoneDayjs(dateString);
  return {
    hours: d.hour() % 12,
    minutes: d.minute(),
    period: d.format("A") as Period,
  };
};

/**
 * Determine AM/PM period from a native Date object using local time.
 *
 * @param date - JS Date object
 * @returns `"AM"` if hours < 12, otherwise `"PM"`
 */
export const getPeriodFromDate = (date: Date): Period =>
  date.getHours() >= 12 ? PERIODS.PM : PERIODS.AM;

/**
 * Extract 12-hour time parts from a native Date object.
 *
 * @param date - JS Date object
 * @param utc - When `true`, use UTC getters (`getUTCHours`/`getUTCMinutes`).
 * @returns 12-hour time parts
 */
export const parseTimeFromDate = (
  date: Date,
  utc?: boolean
): { hours: number; minutes: number; period: Period } => ({
  hours: (utc ? date.getUTCHours() : date.getHours()) % 12,
  minutes: utc ? date.getUTCMinutes() : date.getMinutes(),
  period: getPeriodFromDate(date),
});

/**
 * Convert 12-hour time parts to 24-hour hours.
 * Hours are normalized to 0–11 before conversion so that out-of-range
 * values (e.g. 12) produce valid 24-hour hours (0–23) instead of
 * overflowing to 24.
 *
 * @param hours - 12-hour hour value (0–11, where 0 = 12)
 * @param period - `"AM"` or `"PM"`
 * @returns 24-hour hour (0–23)
 */
export const toStandardHours = (hours: number, period: Period): number => {
  const h = hours % 12; // normalize to 0–11 range first
  return period === PERIODS.PM ? h + 12 : h;
};

/**
 * Build grouped options for the searchable timezone Select.
 * US pinned zones appear first in a "Suggested" group; remaining zones appear
 * in an "All time zones" group. Pinned zones are deduplicated — they never
 * appear in both groups.
 *
 * @param timeZones - Available timezone display strings (from GraphQL response)
 * @returns Option groups compatible with antd {@link Select} (nested `options` arrays)
 */
export const buildTimezoneOptions = (timeZones: TimeZoneValue[]) => {
  const pinned = US_PINNED_ZONES.filter((z) => timeZones.includes(z));
  const remaining = timeZones.filter((z) => !US_PINNED_ZONES.includes(z));
  const groups: Array<{
    label: string;
    options: { label: string; value: string }[];
  }> = [];
  if (pinned.length) {
    groups.push({
      label: "Suggested",
      options: pinned.map((z) => ({ label: z, value: z })),
    });
  }
  if (remaining.length) {
    groups.push({
      label: "All time zones",
      options: remaining.map((z) => ({ label: z, value: z })),
    });
  }
  return groups;
};

/**
 * Default ISO date string used when no dateString is provided to
 * {@link TimePickerFromDateString}. Produces today at 08:00:00.000Z.
 * The value is computed once at module load time (matching the legacy
 * `defaultProps` behavior), so it does not update as time passes.
 */
export const DEFAULT_DATE_STRING = dayjs
  .utc()
  .hour(8)
  .minute(0)
  .second(0)
  .millisecond(0)
  .toISOString();

/**
 * Shape of the object emitted by hour and minute change handlers.
 * Maintained for backward compatibility with all consuming components.
 */
export interface HourMinuteOption {
  value: number;
  label: string;
}

/**
 * Zero-pad a single-digit number to two digits.
 * Used by {@link TimePickerFromScheduleTime} when building "HHmm" strings.
 *
 * @param number - Number to pad (expected 0–59)
 * @returns Zero-padded string (e.g. `5` → `"05"`, `12` → `"12"`)
 */
export const padTwoDigitTime = (number: number): string =>
  number < 10 ? `0${number}` : `${number}`;
